import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import axios from 'axios';

import tr from './locales/tr.json';
import en from './locales/en.json';
import ru from './locales/ru.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import es from './locales/es.json';
import trQuotes from './locales/tr/quotes.json';
import enQuotes from './locales/en/quotes.json';
import deQuotes from './locales/de/quotes.json';
import frQuotes from './locales/fr/quotes.json';
import esQuotes from './locales/es/quotes.json';

/**
 * Paydaai i18n bootstrap.
 *
 * Priority for the initial language pick (higher wins):
 *   1) URL query `?lang=tr` / `?lang=en` (e.g. deep-links from the marketing
 *      site). Supported quote locales are honoured; unsupported values are ignored.
 *   2) The user's saved language in `localStorage["paydaai_lang_v1"]`.
 *   3) Navigator language (falls back to Turkish otherwise).
 *   4) Hard fallback: Turkish.
 *
 * After login, `AuthContext.checkAuth` overrides the choice with the
 * authenticated user's `profile.language` so their preference wins on
 * subsequent visits.
 */
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      tr: { translation: tr, quotes: trQuotes },
      en: { translation: en, quotes: enQuotes },
      de: { translation: de, quotes: deQuotes },
      fr: { translation: fr, quotes: frQuotes },
      es: { translation: es, quotes: esQuotes },
      ru: { translation: ru, quotes: trQuotes },
    },
    ns: ['translation', 'quotes'],
    defaultNS: 'translation',
    fallbackNS: 'quotes',
    fallbackLng: { de: ['en'], fr: ['en'], es: ['en'], ru: ['tr'], default: ['tr'] },
    supportedLngs: ['tr', 'en', 'de', 'fr', 'es', 'ru'],
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'paydaai_lang_v1',
      caches: ['localStorage'],
    },
    returnNull: false,
  });

// Older quote endpoints can return Turkish validation text. Localize known
// reason codes and use an English fallback for unstructured server failures.
// Do not change `reason`, HTTP status, or validation field details.
axios.interceptors.response.use(undefined, (error) => {
  const url = error.config?.url || '';
  const activeLang = (i18n.resolvedLanguage || i18n.language || 'tr').split('-')[0];
  if (activeLang !== 'tr'
      && /\/api\/(?:quote|staff)(?:\/|$)/.test(url) && error.response?.data) {
    const detail = error.response.data.detail;
    const reason = detail && typeof detail === 'object' ? detail.reason : null;
    const reasonKeys = {
      catalog_empty: 'ai.catalogEmpty',
      missing_prices: 'ai.missingPricesToast',
      ai_timeout: 'ai.timeout',
      ai_error: 'ai.genericError',
      multi_package_locked: 'ai.multiPackageLocked',
    };
    // Newer endpoints send { reason, params } and keep their translations
    // under quotes:errors.<reason> (plan_required uses the feature name).
    const params = (detail && typeof detail === 'object')
      ? (detail.params || (detail.limit != null ? { limit: detail.limit } : {}))
      : {};
    const errorKey = reason === 'plan_required' && detail.feature
      ? `errors.plan_required_${detail.feature}`
      : `errors.${reason}`;
    let message;
    if (reasonKeys[reason]) message = i18n.t(reasonKeys[reason], { ns: 'quotes' });
    else if (reason && i18n.exists(errorKey, { ns: 'quotes' })) message = i18n.t(errorKey, { ns: 'quotes', ...params });
    else message = i18n.t('common.error', { ns: 'quotes' });
    if (typeof detail === 'string') error.response.data.detail = message;
    else if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
      error.response.data.detail = { ...detail, message };
    }
  }
  return Promise.reject(error);
});

// P8 — URL `?lang=` handling.
// The querystring detector reads the value at boot, but we also need to
// (a) validate the value against the supported list, (b) persist it to
// localStorage so the choice survives navigation, and (c) strip the
// query param after use so refresh doesn't keep re-applying it.
if (typeof window !== 'undefined') {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get('lang') || '').toLowerCase();
    if (['tr', 'en', 'de', 'fr', 'es'].includes(raw)) {
      if (i18n.resolvedLanguage !== raw) i18n.changeLanguage(raw);
      try { localStorage.setItem('paydaai_lang_v1', raw); } catch { /* noop */ }
      // Flag the URL-provided language so AuthContext.checkAuth can persist
      // it to the authenticated profile once the user is logged in.
      try { sessionStorage.setItem('paydaai_lang_pending_v1', raw); } catch { /* noop */ }
      // Remove the query param without reloading so it doesn't stick.
      params.delete('lang');
      const qs = params.toString();
      const nextUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      try { window.history.replaceState({}, '', nextUrl); } catch { /* noop */ }
    }
  } catch { /* URL parsing not critical for bootstrap */ }
}

export default i18n;
